import { useState } from 'react';
import { useUploader } from '@masters-union/union-stack/react';
import imageCompression from 'browser-image-compression';

const useMultiUpload = () => {
  const { upload, cancel, progress } = useUploader();
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [fileUploadSuccess, setFileUploadSuccess] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [uploadedFileData, setUploadedFileData] = useState([]);

  const resetState = () => {
    setIsFileUploading(false);
    setFileUploadSuccess(false);
    setFileUploadError(null);
    setUploadedFileData([]);
  };

  const uploadFile = async (files, fileFor) => {
    try {
      resetState();
      setIsFileUploading(true);

      // Accepts a FileList or a plain array of Files.
      const fileList = Array.from(files ?? []);
      if (fileList.length === 0) {
        throw new Error("No files selected for upload");
      }

      let compressedFiles;
      try {
        compressedFiles = await Promise.all(
          fileList.map((file) =>
            imageCompression(file, {
              maxSizeMB: 1,
              useWebWorker: true,
              initialQuality: 1.0,
              alwaysKeepResolution: true
            })
          )
        );
      } catch (err) {
        throw new Error("Error compressing file");
      }

      // upload() resolves with the batch result, so the state below settles in
      // one pass — consumers watching isFileUploading see the data already set.
      const { filesUploaded, filesFailed } = await upload(compressedFiles, {
        metadata: fileFor !== undefined ? { fileFor } : undefined
      });

      if (!filesUploaded?.length) {
        throw new Error(filesFailed?.[0]?.error?.message || "Error uploading file");
      }

      setUploadedFileData(
        filesUploaded.map((uploaded, index) => ({
          name: uploaded.filename || fileList[index]?.name,
          url: uploaded.url,
          size: bytesToSize(uploaded.size ?? fileList[index]?.size),
          fileFor
        }))
      );
      setFileUploadSuccess(true);
    } catch (err) {
      setFileUploadError(err.message);
      setUploadedFileData([]);
      setFileUploadSuccess(false);
    } finally {
      setIsFileUploading(false);
    }
  };

  const bytesToSize = (bytes) => {
    var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    if (bytes === 0) return "n/a";
    var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if (i === 0) return bytes + " " + sizes[i];
    return (bytes / Math.pow(1024, i)).toFixed(1) + " " + sizes[i];
  };

  return {
    isFileUploading,
    fileUploadError,
    fileUploadSuccess,
    uploadedFileData,
    uploadFile,
    resetState,
    uploadProgress: progress,
    cancelUpload: cancel
  };
};

export default useMultiUpload;
